import {
  ALL_SEAT_SIDES,
  OPPOSITE_SEAT_SIDES,
  PREVIOUS_SEAT_SIDES,
  NEXT_SEAT_SIDES,
  PRODUCT_KEYS,
} from '../../constants';
import { getBundleFromSet, ERROR_CODES } from '../stealthTech';
import { status } from '../../../status';

/**
 * Whether a seat can be adjustable.
 *
 * Adjustables carry no geometric requirement of their own: anywhere a seat can go,
 * an adjustable seat can go. The only exclusion is the wedge, which has no
 * adjustable product. This deliberately differs from reclining, which still needs
 * a Side to brace against and at most one free face - see 01-eligibility.md.
 *
 * Kept as one named rule so the add flow, the upgrade panel and the self-heal in
 * Configurator._getVariantSeatCount cannot drift apart. Earlier versions also
 * required bracing here; that made the plus-icon add flow offer almost nowhere,
 * so the constraint was dropped rather than halved.
 *
 * @param {object} item a seat Item
 * @returns {boolean}
 */
export const isAdjustableEligible = (item) =>
  !!item && item._type === 'seat' && item._key !== 'wedge';

/**
 * Arms that continue into another Side on a neighbouring seat.
 *
 * Where an L turns, the corner seat's arm and the Side on the seat around the
 * corner sit on the same world side, forming one straight run down that edge of
 * the sofa. A swept profile only reads correctly at the *end* of such a run -
 * sweeping the inner one leaves two mismatched panels in a line - so these are
 * held at `standard` when the arm type is changed to swept.
 *
 * The reported 4-seat/6-side L, with Y the deep corner seat:
 *
 *      ___ ___ ___
 *     |  A   B   Y  |     <- armLeft on A, armY on Y's right
 *                 D  |    <- D below Y, armD on D's right
 *
 * armY and armD run down the same world edge, so only armLeft may sweep.
 *
 * IMPORTANT: the continuation is matched on being a Side at all, NOT on
 * `style === 'arm'`. postProcessForSet resets every Side in the set to
 * `style = 'back'` and only promotes the two path caps (and M-layout corner
 * sides) back to 'arm'. In the layout above the path caps at armLeft and armY,
 * which is why those two are the ones setArmType converts - and armD, the very
 * panel that makes armY mid-run, is left styled 'back'. Requiring 'arm' on the
 * continuation therefore made this function blind to exactly the case it exists
 * for. The evaluated side is still filtered on 'arm', because convertSidesTypes
 * only ever rewrites those.
 *
 * Returns ids rather than items because convertSidesTypes applies the result to
 * serialized JSON, where edges are instance ids rather than object references.
 *
 * @param {object[]} items every placed item
 * @returns {Set<string>} ids of arm Sides that have a collinear neighbour
 */
export const getCollinearArmIds = (items) => {
  const collinear = new Set();

  items.forEach((side) => {
    if (side._type !== 'side' || side.style !== 'arm') return;

    // a Side always attaches to its seat through its own `bottom` edge
    const seat = side.bottom;
    if (!seat || seat._type !== 'seat') return;

    const armLocalSide = ALL_SEAT_SIDES.find((s) => seat[s] === side);
    if (!armLocalSide) return;
    const armWorldSide = seat.getWorldSide(armLocalSide);
    if (!armWorldSide) return;

    // look along the run: the seats perpendicular to this arm
    const continues = [
      NEXT_SEAT_SIDES[armLocalSide],
      PREVIOUS_SEAT_SIDES[armLocalSide],
    ].some((perpendicular) => {
      const neighbour = seat[perpendicular];
      if (!neighbour || neighbour._type !== 'seat') return false;

      // ...carrying a Side facing the same way. Any Side continues the run:
      // see the note above on why style must not be checked here.
      return ALL_SEAT_SIDES.some((s) => {
        const candidate = neighbour[s];
        return (
          candidate &&
          candidate._type === 'side' &&
          neighbour.getWorldSide(s) === armWorldSide
        );
      });
    });

    if (continues) collinear.add(side._id);
  });

  return collinear;
};

const sortSeatsAttachments = (seat, ignoreItemSet = new Set([])) => {
  const connections = ALL_SEAT_SIDES.filter((side) => {
    return !!seat[side];
  }).map((localSide) => ({
    localSide,
    target: seat[localSide],
    targetLocalSide: ALL_SEAT_SIDES.find(
      (side) => seat[localSide][side] === seat
    ),
  }));
  const result = {
    seats: [],
    sides: [],
    anytables: [],
  };

  connections.forEach((connection) => {
    const key = `${connection.target._type}s`;
    if (
      result[key] !== undefined &&
      !ignoreItemSet.has(connection.target._id)
    ) {
      result[key].push(connection);
    }
  });

  return result;
};
/**
 * Given a seat item, check the side by its sideConnector if this side is on the
 * left/right hand of a layout (or the back of a 1 seat 3 sides)
 * @param {object} seatItem
 * @param {object} sideConnector
 * @returns
 */
const getArmPosition = (seatItem, sideConnector) => {
  let side;
  if (sideConnector && sideConnector.localSide) {
    const nextItem = seatItem[NEXT_SEAT_SIDES[sideConnector.localSide]];
    const prevItem = seatItem[PREVIOUS_SEAT_SIDES[sideConnector.localSide]];
    if (nextItem && !prevItem) {
      // next side connects to something while previous
      // side does not
      if (nextItem._type === 'side') {
        // it's a side
        side = 'right';
      } else {
        // it's a seat (ottoman)
        side = 'left';
      }
    } else if (prevItem && !nextItem) {
      // previous side connects to something while next
      // side does not
      if (prevItem._type === 'side') {
        // it's a side
        side = 'left';
      } else {
        // it's a seat (ottoman)
        side = 'right';
      }
    } else if (prevItem && nextItem) {
      // both sides connects to something
      if (nextItem._type !== prevItem._type) {
        if (nextItem._type === 'side') {
          // it's a side
          side = 'right';
        } else {
          // it's a seat (ottoman)
          side = 'left';
        }
      } else if (nextItem._type === 'side') {
        // back on single seat with ottoman
        side = 'back';
      }
    }
  }
  return side;
};

/**
 * Get the connection status of all seats
 * @param {*} items
 * @param {*} ignoreItems
 * @returns
 */
const getConnectedSets = (items, ignoreItems = []) => {
  const sets = [];
  const ignoreItemSet = new Set(ignoreItems);

  // split items into seats and sides
  const { seats, sides, connections } = items.reduce(
    (acc, item) => {
      const id = item._id;
      if (!ignoreItemSet.has(id)) {
        if (item._type === 'seat') acc.seats.push(item);
        if (item._type === 'side') acc.sides.push(item);
        acc.connections[id] = item
          .getConnections()
          .reduce((result, connection) => {
            const { target, alignment, targetLocalSide } = connection;
            if (!ignoreItemSet.has(target._id))
              result[connection.localSide] = {
                target,
                targetLocalSide,
              };
            return result;
          }, {});
      }
      return acc;
    },
    { seats: [], sides: [], connections: {} }
  );
  const deemedConnections = {};
  if (seats.length < 1) {
    return sets;
  }

  // find next seat
  const traverseSeats = (seatItem, set) => {
    const seatId = seatItem._id;
    if (ignoreItemSet.has(seatId)) return;
    if (!set.seats.has(seatId)) set.seats.set(seatId, seatItem);
    if (!set.state.hasStdSeat && seatItem._key === 'standard') {
      set.state.hasStdSeat = true;
    }

    const connectedSeats = [];
    const connectedSides = [];
    deemedConnections[seatId] = {};
    ALL_SEAT_SIDES.forEach((side) => {
      const connection = connections[seatId][side];
      if (connection) {
        const tempConnection = { ...connection };
        while (
          tempConnection.target._type === 'anytable' ||
          (tempConnection.target._type === 'side' &&
            tempConnection.target._key === 'doubleSided')
        ) {
          const map = set[`${tempConnection.target._type}s`];
          if (!map.has(tempConnection.target._id)) {
            map.set(tempConnection.target._id, tempConnection.target);
          }
          const nextTarget =
            tempConnection.target[
              OPPOSITE_SEAT_SIDES[tempConnection.targetLocalSide]
            ];
          if (!nextTarget || !connections[nextTarget._id]) break;
          else {
            tempConnection.targetLocalSide = ALL_SEAT_SIDES.find(
              (s) => nextTarget[s]?._id === tempConnection.target._id
            );
            tempConnection.target = nextTarget;
          }
        }
        deemedConnections[seatId][side] = tempConnection;
        switch (tempConnection.target._type) {
          case 'seat':
            connectedSeats.push({ ...tempConnection, localSide: side });
            break;
          case 'side':
            connectedSides.push({ ...tempConnection, localSide: side });
            break;
          default:
            break;
        }
      }
    });

    const seat = {
      id: seatId,
      item: seatItem,
    };

    // A pair is a situation where side of a Seat connects to a Side
    // and the opposite is a Seat, which implies this Seat could be
    // end/corner of a layout. Each pair stores the connectors of
    // the seat item that connect to the other seat/side
    const pairs = [];

    // Check surrounding seats and add to seat's side
    if (connectedSeats.length) {
      const isNotCenterSeat =
        connectedSides && connectedSeats.length - connectedSides.length < 2;
      connectedSeats.forEach((seatAttachment) => {
        const { localSide, target, targetLocalSide } = seatAttachment;

        seat[localSide] = seatAttachment;

        if (connectedSides && isNotCenterSeat) {
          const findSide = connectedSides.find(
            (attachment) =>
              attachment.localSide === OPPOSITE_SEAT_SIDES[localSide]
          );
          if (findSide) {
            pairs.push({
              seatConnector: seatAttachment,
              sideConnector: findSide,
            });
          }
        }

        if (!set.seats.has(target._id)) {
          traverseSeats(target, set);
        }
      });
    }
    // Add surrounding side's connectors to seat's side
    if (connectedSides) {
      connectedSides.forEach((sideAttachment) => {
        const { localSide, target, targetLocalSide } = sideAttachment;
        seat[localSide] = sideAttachment;
        if (!set.sides.has(target._id)) {
          set.sides.set(target._id, target);
          if (!set.state.hasRollArm && target._key === 'rollArm') {
            set.state.hasRollArm = true;
          } else if (
            !set.state.hasSweptArm &&
            target._key === PRODUCT_KEYS.SWEPTARM
          ) {
            set.state.hasSweptArm = true;
          }
        }
      });
    }

    if (pairs.length > 0) {
      seat.pairs = pairs;
    }

    set.mappedSeats[seatId] = seat;
  };

  seats.forEach((seat) => {
    const handled = sets.some((set) => set.seats.has(seat._id));
    if (handled) return;
    const set = {
      seats: new Map([]),
      sides: new Map([]),
      anytables: new Map([]),
      mappedSeats: {},
      state: {},
      paths: [],
      errors: [], // list of error codes why this set is not eligible for stealthTech
    };
    traverseSeats(seat, set);
    sets.push(set);
  });

  sets.sort((a, b) => {
    if (b.seats.size === a.seats.size) {
      return b.sides.size - a.sides.size;
    } else {
      return b.seats.size - a.seats.size;
    }
  });

  console.log('sets', sets);

  return sets;
};

const postProcessForSet = (set) => {
  const { sides, seats, mappedSeats, paths, stealthTech } = set;
  Array.from(sides.values()).forEach((side) => {
    side.style = 'back';
    if (/angled/.test(side._key)) {
      side._configurator.setConfiguration({
        PillowPosition: 'none',
      });
      side.pillows = [];
    }
  });
  const path = stealthTech ? stealthTech.bundle?.path : paths[0];

  set.cornerArms = [];

  if (path) {
    if (path.length === 1) {
      const item = path[0];
      const seat = item.item;
      const side = item.cap.target;
      const nextItem = seat[NEXT_SEAT_SIDES[item.cap.localSide]];
      const prevItem = seat[PREVIOUS_SEAT_SIDES[item.cap.localSide]];
      if (item.cap.armSide === 'back') {
        side.style = 'back';
        if (nextItem) {
          nextItem.style = 'arm';
          if (/angled/.test(nextItem._key)) {
            nextItem._configurator.setConfiguration({
              PillowPosition: 'right',
            });
          } else if (/sweptArm/.test(nextItem._keyAlians || nextItem._key)) {
            nextItem.ArmSide = 'left';
            nextItem._configurator.setConfiguration({
              ArmSide: 'left',
            });
          }
        }
        if (prevItem) {
          prevItem.style = 'arm';
          if (/angled/.test(prevItem._key)) {
            prevItem._configurator.setConfiguration({
              PillowPosition: 'left',
            });
          } else if (/sweptArm/.test(prevItem._keyAlians || prevItem._key)) {
            prevItem.ArmSide = 'right';
            prevItem._configurator.setConfiguration({
              ArmSide: 'right',
            });
          }
        }
      }
    } else {
      const firstItem = path[0];
      const lastItem = path[path.length - 1];
      const { layout } = lastItem;

      [firstItem, lastItem].forEach((item) => {
        const seat = item.item;
        const side = item.cap.target;
        side.style = 'arm';
        if (/angled/.test(side._key)) {
          side._configurator.setConfiguration({
            PillowPosition: OPPOSITE_SEAT_SIDES[item.cap.armSide],
          });
        } else if (/sweptArm/.test(side._keyAlians || side._key)) {
          side.ArmSide = item.cap.armSide;
          side._configurator.setConfiguration({
            ArmSide: item.cap.armSide,
          });
        }

        seat._updatePillow({ noDeepPillow: layout === 'M' });
      });

      const cornerItems = path.filter((node) => node.type === 'corner');
      // M shape check corner piece's cap
      if (layout === 'M') {
        if (cornerItems.length !== 3) {
          console.warn('M layout should have 3 corner pieces');
        } else {
          const firstCorner = cornerItems[0];
          const firstCornerItem = firstCorner.item;
          const firstItemLookupSide = NEXT_SEAT_SIDES[firstCorner.oppositeSide];
          if (firstCornerItem[firstItemLookupSide]?._type === 'side') {
            const side = firstCornerItem[firstItemLookupSide];
            side.style = 'arm';
            set.cornerArms.push(side);
            if (/angled/.test(side._key)) {
              const armSide = getArmPosition(
                firstCornerItem,
                mappedSeats[firstCornerItem._id][firstItemLookupSide]
              );
              side._configurator.setConfiguration({
                PillowPosition: OPPOSITE_SEAT_SIDES[armSide],
              });
              if (OPPOSITE_SEAT_SIDES[armSide])
                side.pillows.push({ type: 'sidePillow', key: 'angled' });
            } else if (/sweptArm/.test(side._keyAlians || side._key)) {
              const armSide = getArmPosition(
                firstCornerItem,
                mappedSeats[firstCornerItem._id][firstItemLookupSide]
              );
              side.ArmSide = armSide;
              side._configurator.setConfiguration({
                ArmSide: armSide,
              });
            }
            firstCornerItem._updatePillow({ noDeepPillow: true });
          }
          const lastCorner = cornerItems[cornerItems.length - 1];
          const lastCornerItem = lastCorner.item;
          const lastItemLookupSide = lastCorner.oppositeSide;
          if (lastCornerItem[lastItemLookupSide]?._type === 'side') {
            const side = lastCornerItem[lastItemLookupSide];
            side.style = 'arm';
            set.cornerArms.push(side);
            if (/angled/.test(side._key)) {
              const armSide = getArmPosition(
                lastCornerItem,
                mappedSeats[lastCornerItem._id][lastItemLookupSide]
              );
              side._configurator.setConfiguration({
                PillowPosition: OPPOSITE_SEAT_SIDES[armSide],
              });
              if (OPPOSITE_SEAT_SIDES[armSide])
                side.pillows.push({ type: 'sidePillow', key: 'angled' });
            } else if (/sweptArm/.test(side._keyAlians || side._key)) {
              const armSide = getArmPosition(
                lastCornerItem,
                mappedSeats[lastCornerItem._id][lastItemLookupSide]
              );
              side.ArmSide = armSide;
              side._configurator.setConfiguration({
                ArmSide: armSide,
              });
            }
            lastCornerItem._updatePillow({ noDeepPillow: true });
          }
        }
      } else {
        cornerItems.forEach((item) =>
          item.item._updatePillow({ isCorner: true })
        );
      }
    }
  } else {
    // Only set arm type when there is a path(valid layout)
  }

  const sweptBacks = Array.from(sides.values()).filter((side) => {
    if (side._key === PRODUCT_KEYS.SWEPTARM && side.style === 'back') {
      // TODO highlight the swept arm
      return true;
    }
    return false;
  });

  if (sweptBacks.length) {
    set.errors.push(ERROR_CODES.SWEPTARM_BACK);
  }
};

const COMMON_LAYOUT_TYPES = ['I', 'L', 'U', 'M'];

const LAYOUT_FUNCS = [
  // M, U, L, I
  (set) => {
    const { seats, sides: allSides, mappedSeats, state, errors, paths } = set;

    // Get seats that are end/corner(s)
    const endSeats = Object.values(mappedSeats).filter((s) => !!s.pairs);

    /**
     * Check seat and see if it connects to another seat
     * @param {*} headNode node in path as the head
     * @param {*} connector the connector the tail starts from
     * @param {*} numOfCorners
     */
    function getTailSeats(headNode, connector, numOfCorners = 0) {
      const { type } = headNode;
      const { target: item, targetLocalSide: seatSide } = connector;
      const { _id: seatId } = item;
      // Item could be a doubleSided side which has a seat on the other side,
      // this case it can be treated connected
      const paths = [];
      if (mappedSeats[seatId]) {
        // look down the line
        const oppositeSide = OPPOSITE_SEAT_SIDES[seatSide];
        const oppositeConnector = mappedSeats[seatId][oppositeSide];

        if (oppositeConnector) {
          if (oppositeConnector.target._type === 'anytable') {
            let nextSide =
              OPPOSITE_SEAT_SIDES[oppositeConnector.targetLocalSide];
            let nextItem = oppositeConnector.target[nextSide];
            let curItem = oppositeConnector.target;
            while (nextItem && nextItem._type !== 'seat') {
              const side = ALL_SEAT_SIDES.find(
                (s) => nextItem[s]?._id === curItem._id
              );
              nextSide = OPPOSITE_SEAT_SIDES[side];
              curItem = nextItem;
              nextItem = curItem[nextSide];
            }
          } else if (
            mappedSeats[seatId].pairs &&
            oppositeConnector.target._type === 'side'
          ) {
            if (
              oppositeConnector.target._key === 'doubleSided' &&
              oppositeConnector.target[
                OPPOSITE_SEAT_SIDES[oppositeConnector.targetLocalSide]
              ]?._type === 'seat'
            ) {
              const nextSeat =
                oppositeConnector.target[
                  OPPOSITE_SEAT_SIDES[oppositeConnector.targetLocalSide]
                ];
              const tails = getTailSeats(
                headNode,
                {
                  localSide:
                    OPPOSITE_SEAT_SIDES[oppositeConnector.targetLocalSide],
                  target: nextSeat,
                  targetLocalSide: ALL_SEAT_SIDES.find(
                    (s) => nextSeat[s]?._id === oppositeConnector.target._id
                  ),
                },
                numOfCorners
              );
              const node = {
                type: 'middle',
                id: seatId,
                item: mappedSeats[seatId].item,
              };
              if (tails.length) {
                tails.forEach((tail) => {
                  paths.push([node].concat(tail));
                });
              }
            } else {
              // This seat itself is an end/corner
              paths.push([
                {
                  type: 'end',
                  id: seatId,
                  item: mappedSeats[seatId].item,
                  cap: oppositeConnector,
                  layout: COMMON_LAYOUT_TYPES[numOfCorners],
                },
              ]);
            }
          }
          // The item connected is another seat
          else if (mappedSeats[oppositeConnector.target._id]) {
            if (item._key === 'wedge') {
              const previousConnector = mappedSeats[seatId][seatSide];
              const previousItem = previousConnector.target;

              let nodeType;
              if (previousItem._key === item._key) {
                if (
                  previousConnector.localSide === OPPOSITE_SEAT_SIDES[seatSide]
                ) {
                  if (type === 'corner') {
                    // Two/more wedge seats treat as one corner
                    nodeType = 'corner-ext';
                  } else nodeType = 'corner';
                } else {
                  nodeType = 'middle';
                }
              } else {
                nodeType = 'corner';
              }

              const isCorner = nodeType === 'corner';

              const node = {
                type: nodeType,
                id: seatId,
                item: mappedSeats[seatId].item,
              };

              const tails = getTailSeats(
                node,
                oppositeConnector,
                numOfCorners + (isCorner ? 1 : 0)
              );
              if (tails.length) {
                tails.forEach((tail) => {
                  paths.push([node].concat(tail));
                });
              }
            } else {
              const node = {
                type: 'middle',
                id: seatId,
                item,
              };
              const tails = getTailSeats(node, oppositeConnector, numOfCorners);
              if (tails.length) {
                tails.forEach((tail) => {
                  paths.push([node].concat(tail));
                });
              }
            }
          }
        }

        // 1 step counter clockwisely
        const previousSide = PREVIOUS_SEAT_SIDES[oppositeSide];
        const previousConnector = mappedSeats[seatId][previousSide];
        if (previousConnector && mappedSeats[previousConnector.target._id]) {
          const node = {
            type: 'corner',
            id: seatId,
            clockwise: false,
            item,
            oppositeSide,
          };
          const tails = getTailSeats(node, previousConnector, numOfCorners + 1);
          if (tails.length) {
            tails.forEach((tail) => {
              paths.push([node].concat(tail));
            });
          }
        }

        // 1 step clockwisely
        const nextSide = NEXT_SEAT_SIDES[oppositeSide];
        const nextConnector = mappedSeats[seatId][nextSide];
        if (nextConnector && mappedSeats[nextConnector.target._id]) {
          const node = {
            type: 'corner',
            id: seatId,
            clockwise: true,
            item,
            oppositeSide,
          };
          const tails = getTailSeats(node, nextConnector, numOfCorners + 1);
          if (tails.length) {
            tails.forEach((tail) => {
              paths.push([node].concat(tail));
            });
          }
        }
      } else {
        // this may be an anytable
        let nextSide = OPPOSITE_SEAT_SIDES[seatSide];
        let nextItem = connector.target[nextSide];
        let curItem = connector.target;
        while (nextItem && nextItem._type !== 'seat') {
          const side = ALL_SEAT_SIDES.find(
            (s) => nextItem[s]?._id === curItem._id
          );
          nextSide = OPPOSITE_SEAT_SIDES[side];
          curItem = nextItem;
          nextItem = curItem[nextSide];
        }
        if (nextItem?._type === 'seat') {
          const c = {
            ...connector,
            target: nextItem,
            targetLocalSide: ALL_SEAT_SIDES.find(
              (s) => nextItem[s]?._id === curItem._id
            ),
          };
          const tails = getTailSeats(headNode, c, numOfCorners);
          if (tails.length) {
            tails.forEach((tail) => {
              paths.push(tail);
            });
          }
        }
      }

      return paths;
    }

    // clockwisely to get possible layouts
    endSeats.forEach((endSeat) => {
      const { id, pairs } = endSeat;
      pairs.forEach((pair) => {
        const { seatConnector, sideConnector } = pair;
        const { item } = mappedSeats[id];

        const armSide = getArmPosition(item, sideConnector);
        const node = {
          type: 'end',
          id,
          item,
          cap: { ...mappedSeats[id][sideConnector.localSide], armSide },
        };

        const tails = getTailSeats(node, seatConnector, 0);
        if (tails.length) {
          tails.forEach((tail) => {
            const path = [{ ...node }].concat(tail);
            const checkPathNodeIds = path.map((p) => p.id).reverse();
            // check existing paths and see if current path
            // is a reversed one already exists
            if (
              !paths.some((p) => {
                if (p.length === path.length) {
                  const ids = p.map((e) => e.id);
                  for (let i = 0; i < p.length; i++) {
                    if (ids[i] !== checkPathNodeIds[i]) return false;
                  }
                  return true;
                }
                return false;
              })
            ) {
              // This is a unique path
              paths.push(path);
            }
          });
        } else if (endSeats.length === 1) {
          paths.push([node]);
        }
      });
    });

    // Also generate a path when there is only one seat
    if (seats.size === 1) {
      const seat = seats[0];
      let sideIndex = ALL_SEAT_SIDES.findIndex((side) => seat[side]);
      if (sideIndex !== -1) {
        const node = {
          id: seat._id,
          item: seat,
          type: 'end',
          cap: { ...mappedSeats[seat._id][ALL_SEAT_SIDES[sideIndex]] },
        };
        for (; sideIndex < ALL_SEAT_SIDES.length; sideIndex++) {
          const side = ALL_SEAT_SIDES[sideIndex];
          const nextSide = NEXT_SEAT_SIDES[side];
          const prevSide = PREVIOUS_SEAT_SIDES[side];
          if (seat[prevSide] && seat[side] && seat[nextSide]) {
            node.cap = mappedSeats[seat._id][side];
            break;
          }
        }
        paths.push([node]);
      }
    }

    // Sort the paths by the length of nodes
    const sortedPaths = paths.sort((a, b) => b.length - a.length);
    paths.forEach((path) => {
      // sort nodes so the rightmost seat that has an arm stays the first element
      // in the array
      const { layout } = path[path.length - 1];
      const corners = path.filter((n) => n.type === 'corner');
      const firstCorner = corners[0];
      if (
        (firstCorner && !firstCorner.clockwise) ||
        (!firstCorner && path[0].cap.armSide !== 'right')
      ) {
        corners.forEach((node) => {
          node.clockwise = !node.clockwise;
        });
        path[0].layout = layout;
        path.reverse();
        delete path[0].layout;
      }
    });

    const setPathArms = (path) => {
      if (!path) return;
      const firstItem = path[0];
      const lastItem = path[path.length - 1];
      if (!firstItem.cap.armSide && !lastItem.cap.armSide) {
        firstItem.cap.armSide = 'right';
        lastItem.cap.armSide = 'left';
      } else if (firstItem.cap.armSide && !lastItem.cap.armSide) {
        lastItem.cap.armSide = OPPOSITE_SEAT_SIDES[firstItem.cap.armSide];
      } else if (!firstItem.cap.armSide && lastItem.cap.armSide) {
        firstItem.cap.armSide = OPPOSITE_SEAT_SIDES[lastItem.cap.armSide];
      }
    };

    // Island has at least a valid layout, use it to determine arm side
    const bundle = getBundleFromSet(set);
    if (bundle) {
      bundle.speakers.forEach((item) => {
        item.type = allSides.get(item.id)._key;
      });
      bundle.satellites.forEach((item) => {
        item.type = allSides.get(item.id)._key;
      });
      set.stealthTech = {
        bundle,
      };
      setPathArms(bundle.path);
    } else setPathArms(sortedPaths[0]);

    set.paths = sortedPaths;
    return set;
  },
  // I: 1 main seat,  x ottoman
  (set) => {
    const { seats, mappedSeats, state, paths } = set;
    const errors = [];

    // Get seats that only connects 1 seat
    const endSeats = Object.values(mappedSeats).filter((s) => !!s.pairs);

    if (seats.size === 1) {
      const seatItem = Array.from(seats.values())[0];
      const { sides: connectedSides } = sortSeatsAttachments(seatItem);

      const isWedge = seatItem._key === 'wedge';

      const seat = {};
      const oppositeSides = [];
      if (connectedSides && !isWedge) {
        connectedSides.forEach((seatAttachment) => {
          const { localSide } = seatAttachment;
          if (seat[localSide]) {
            return; // side handled
          }
          seat[localSide] = seatAttachment;
          const otherSide = OPPOSITE_SEAT_SIDES[localSide];
          const findSide = connectedSides.find(
            (attachment) => attachment.localSide === otherSide
          );
          if (findSide) {
            oppositeSides.push({
              [otherSide]: findSide,
              [localSide]: seatAttachment,
            });
            seat[otherSide] = findSide;
          }
        });
      }
      if (oppositeSides.length === 1) {
        const speakers = Object.entries(oppositeSides[0]).map(
          ([side, connector]) => {
            delete seat[side];
            return { id: connector.target._id, type: connector.target._key };
          }
        );

        const back = ALL_SEAT_SIDES.find((side) => seat[side]);
        if (back) {
          paths.push([
            {
              id: seatItem._id,
              item: seatItem,
              type: 'end',
              cap: { ...seat[back], armSide: 'back' },
            },
          ]);
        }
        if (
          speakers[0].type === speakers[1].type &&
          speakers[0].type !== 'rollArm'
        ) {
          set.stealthTech = {
            bundle: {
              layout: 'I',
              subwoofer: seatItem._id,
              speakers,
              satellites: [],
              path: paths[0],
            },
          };
        } else if (state.hasRollArm) {
          errors.push(ERROR_CODES.ARMS_DONT_MATCH);
        }
      } else {
        if (state.hasRollArm) errors.push(ERROR_CODES.NO_END_STANDARD_SIDES);
        if (isWedge) errors.push(ERROR_CODES.WEDGE_SEAT_INELIGIBLE);
        if (errors.length)
          return {
            errors,
          };
        else return;
      }
    } else if (endSeats.length === 1) {
      const item = endSeats[0];
      const { id, pairs } = item;
      if (pairs.length === 1) {
        const backSide = ALL_SEAT_SIDES.find(
          (s) => s === pairs[0].sideConnector.localSide
        );

        const leftSide = NEXT_SEAT_SIDES[backSide];
        const rightSide = PREVIOUS_SEAT_SIDES[backSide];

        if (item[leftSide] && item[rightSide]) {
          const speakers = [leftSide, rightSide]
            .map((side) => {
              const { target } = item[side];
              if (target._type === 'side' && target._key !== 'rollArm')
                return { id: target._id, type: target._key };
            })
            .filter((e) => !!e);
          if (speakers.length === 2)
            set.stealthTech = {
              bundle: {
                layout: 'I',
                subwoofer: id,
                speakers,
                satellites: [],
                path: [
                  {
                    cap: { ...pairs[0].sideConnector, armside: 'back' },
                    id: item._id,
                    item,
                    type: 'end',
                  },
                ],
              },
            };
        }
      }
    }
    if (!set.stealthTech && state.hasRollArm) {
      errors.push(ERROR_CODES.NO_END_STANDARD_SIDES);
    }
    set.errors = errors;
    return set;
  },
];

const evaluateSets = (sets) => {
  const ottomans = {};
  sets.map((set) => {
    const endSeats = Object.values(set.mappedSeats).filter((s) => !!s.pairs);
    const result =
      endSeats.length < 2 ? LAYOUT_FUNCS[1](set) : LAYOUT_FUNCS[0](set);
    console.log('Evaluated', result);
    const { mappedSeats, paths } = set;
    // const corners =
    //   paths.length > 0
    //     ? paths[0].filter((n) => {
    //         const deemedCorner = n.type === 'corner';
    //         const connectingSeats = ALL_SEAT_SIDES.map((s) =>
    //           n.item[s]?._type === 'seat' ? n.item[s] : null
    //         ).filter((e) => !!e);

    //         return (
    //           deemedCorner &&
    //           connectingSeats.length > 1 &&
    //           connectingSeats.every((item) =>
    //             ALL_SEAT_SIDES.some((s) => item[s]?._type === 'side')
    //           )
    //         );
    //       })
    //     : [];
    Object.entries(mappedSeats).forEach(([id, obj]) => {
      if (
        !ALL_SEAT_SIDES.some(
          (side) => obj[side] && obj[side].target._type === 'side'
        )
      ) {
        ottomans[id] = obj.item;
      }
      if (
        // not a wedge seat
        obj.item._key !== 'wedge' &&
        // // not a corner seat
        // !corners.some((n) => n.id === id) &&
        // has at least one side
        ALL_SEAT_SIDES.some((s) => obj.item[s]?._type === 'side') &&
        // no more than 1 open sides
        ALL_SEAT_SIDES.reduce((count, s) => {
          if (!obj.item[s]) {
            count++;
          }
          return count;
        }, 0) < 2
      ) {
        obj.ReclineEligible = true;
      }
      // adjustables are graded separately - a looser open-edge allowance
      if (isAdjustableEligible(obj.item)) {
        obj.AdjustableEligible = true;
      }
    });
    if (paths.length) {
      // 2 deep seats 4 sides specific: standard back seat should be deep seat sku
      const path = paths[0];
      if (path.length === 2 && path[path.length - 1]?.layout === 'I') {
        const seatConnSide1 = ['top', 'bottom'].find(
          (side) => path[0].item[side]?._id === path[1].id
        );
        const seatConnSide2 = ['top', 'bottom'].find(
          (side) => path[1].item[side]?._id === path[0].id
        );

        if (
          seatConnSide1 &&
          seatConnSide2 &&
          path[0].item[OPPOSITE_SEAT_SIDES[seatConnSide1]]?._type === 'side' &&
          path[1].item[OPPOSITE_SEAT_SIDES[seatConnSide2]]?._type === 'side'
        ) {
          const prevSideItem1 =
            path[0].item[PREVIOUS_SEAT_SIDES[seatConnSide1]];
          const nextSideItem2 = path[1].item[NEXT_SEAT_SIDES[seatConnSide2]];

          const nextSideItem1 = path[0].item[NEXT_SEAT_SIDES[seatConnSide1]];
          const prevSideItem2 =
            path[1].item[PREVIOUS_SEAT_SIDES[seatConnSide2]];

          if (
            prevSideItem1?._type === 'side' &&
            prevSideItem1?._key !== 'rollArm' &&
            nextSideItem2?._type === 'side' &&
            nextSideItem2?._key !== 'rollArm'
          ) {
            set.convertDeep = [prevSideItem1, nextSideItem2];
          } else if (
            prevSideItem2?._type === 'side' &&
            prevSideItem2?._key !== 'rollArm' &&
            nextSideItem1?._type === 'side' &&
            nextSideItem1?._key !== 'rollArm'
          ) {
            set.convertDeep = [prevSideItem2, nextSideItem1];
          }
        }
      } else if (path.length === 7 && path[path.length - 1]?.layout === 'M') {
        // M - 9 seats 10 sides: middle seat not recommended to be reclined if
        // mixed with standard and deep seats
        const isAlignedAtPathIndex = (index) => {
          const { item } = path[index];
          const { item: prevItem } = path[index - 1];
          const { item: nextItem } = path[index + 1];
          const prevConnection = prevItem
            .getConnections()
            .find((c) => c.target._id === item._id);
          const nextConnection = nextItem
            .getConnections()
            .find((c) => c.target._id === item._id);
          const prevAligned = prevConnection
            ? prevConnection.localSide === prevConnection.targetLocalSide ||
              prevConnection.localSide ===
                OPPOSITE_SEAT_SIDES[prevConnection.targetLocalSide]
            : false;
          const nextAligned = nextConnection
            ? nextConnection.localSide === nextConnection.targetLocalSide ||
              nextConnection.localSide ===
                OPPOSITE_SEAT_SIDES[nextConnection.targetLocalSide]
            : false;
          return prevAligned && nextAligned;
        };
        // Reclining only. Adjustables are deliberately exempt: the rule above
        // is about a recliner mechanism in a jogged M run, and adjustables carry
        // no geometric requirement at all (see isAdjustableEligible). Setting
        // AdjustableEligible here would bypass that rule and make
        // _getVariantSeatCount silently reset these two seats to standard.
        [1, 5].forEach((index) => {
          mappedSeats[path[index].id].ReclineEligible = isAlignedAtPathIndex(
            index
          );
        });
      }
    }
  });

  status.ottomans = ottomans;
};

export {
  getConnectedSets,
  postProcessForSet,
  sortSeatsAttachments,
  evaluateSets,
};
